import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Modal,
  Box,
  SelectChangeEvent,
  Stack,
  FormControl,
  Grid,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  addSecurityControlToAction,
  getSubDomainByActionId,
  removeSubDomainFromAction,
} from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import UseAlert from '../../components/common/alert'
import SecurityControlsSelect from './SecurityCheckFamilyMain'
import { SubDomain } from '../../types/subDomainTypes'
import { getSubDomainbySecurityControls } from '../../redux/subDomains/subDomainActions'
import SelectSecurity from '../../components/common/SelectSecurity'
import SelectSubDomain from '../../components/common/selectSubDomain'
import {
  DetailButton,
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'

interface SecurityCheckFamilyListProps {
  ActionId: string
  MissionId: string
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '70%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '12px',
}

const SecurityCheckFamilyListByAction: React.FC<
  SecurityCheckFamilyListProps
> = ({ ActionId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [SecurityControls, setSecurityContols] = useState<string[]>([])
  const [SecurityIds, setSecurityIds] = useState<string[]>([])
  const [SubDomainIds, setSubDomainIds] = useState<string[]>([])
  const [, setSubDomains] = useState<SubDomain[]>([])
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const securityCheckFamilysList = useSelector(
    (state: RootState) => state.securitycheckFamily.subDomainsByActionId
  )
  const loading = useSelector(
    (state: RootState) => state.securitycheckFamily.loading
  )
  const subDomainsbySecurity = useSelector(
    (state: RootState) => state.subDomain.subDomainsbySecurityControls
  )

  useEffect(() => {
    if (ActionId) {
      dispatch(getSubDomainByActionId(ActionId))
    }
  }, [dispatch, ActionId])

  const rows = useMemo(() => {
    const list = Array.isArray(securityCheckFamilysList)
      ? securityCheckFamilysList
      : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter(
      (r: any) =>
        (r.LabelSCF || '').toLowerCase().includes(q) ||
        (r.LabelSD || r.Label || '').toLowerCase().includes(q) ||
        (r.Description || '').toLowerCase().includes(q)
    )
  }, [securityCheckFamilysList, search])

  const handleDeleteClick = async (_id: string) => {
    await dispatch(
      removeSubDomainFromAction({
        SubDomain: { subDomainId: _id },
        _id: ActionId,
      })
    )
    dispatch(getSubDomainByActionId(ActionId))
  }

  const handleSecurityChange1 = (
    event: SelectChangeEvent<string | string[]>
  ) => {
    const newValue = Array.isArray(event.target.value)
      ? event.target.value
      : [event.target.value]
    setSecurityIds(newValue)
  }

  const handleSecurityCheckFamilyChange = (selectedOptions: any) => {
    const newSecurityControls = selectedOptions?.target?.value
      ?.filter((option: any) => option !== undefined && option !== null)
      ?.map((option: any) => {
        if (typeof option === 'object' && option?.value) {
          return option.value
        } else if (typeof option === 'string') {
          return option
        }
        return undefined
      })
      .filter((value: any) => value !== undefined)
    setSecurityContols(newSecurityControls)
  }

  const handleSubDomainChange = (
    newSelectedSubDomainIds: string | string[]
  ) => {
    const subDomainIds = Array.isArray(newSelectedSubDomainIds)
      ? newSelectedSubDomainIds
      : [newSelectedSubDomainIds]
    setSubDomainIds(subDomainIds)
  }

  const SecurityControlsString = SecurityControls.join(',')

  useEffect(() => {
    const fetchSubDomains = async () => {
      try {
        await dispatch(getSubDomainbySecurityControls(SecurityControlsString))
      } catch (error) {
        console.error('Error fetching subdomains:', error)
      }
    }

    if (SecurityControls.length > 0) {
      fetchSubDomains()
    } else {
      setSubDomains([])
    }
  }, [SecurityControls, SecurityControlsString, dispatch])

  useEffect(() => {
    setSubDomains(subDomainsbySecurity)
  }, [subDomainsbySecurity])

  const handleAddSecurityCheckFamilysToAction = async () => {
    try {
      if (SecurityControls && ActionId) {
        await dispatch(
          addSecurityControlToAction({
            securityCheckFamilies: SecurityIds,
            securityControls: SecurityControls,
            subDomains: SubDomainIds,
            _id: ActionId,
          })
        )
        setAlert({
          open: true,
          message: 'SecurityCheckFamily mise à jour avec succès!',
          severity: 'success',
        })
        dispatch(getSubDomainByActionId(ActionId))
        setOpen(false)
      } else {
        setAlert({
          open: true,
          message: 'Veuillez remplir tous les champs.',
          severity: 'warning',
        })
      }
    } catch (error: any) {
      console.error(
        'Erreur lors de la mise à jour de l securityCheckFamily :',
        error.message
      )
      setAlert({
        open: true,
        message:
          'Erreur lors de la mise à jour de l securityCheckFamily. Veuillez réessayer plus tard.',
        severity: 'error',
      })
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <DetailEntityPanel
        listTitle={t('tables.SubDomainsList.title')}
        searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
        searchValue={search}
        onSearchChange={setSearch}
        addLabel={t('buttons.addSecurityControl')}
        onAdd={() => setOpen(true)}
        canAdd={canManageMission(roles)}
      >
        <DetailTable<any>
          loading={loading && !rows.length}
          loadingLabel={t('loading')}
          emptyTitle={t('missionDetailsPage.empty.title', {
            defaultValue: 'Nothing here yet',
          })}
          emptyMessage={t('taskDetailsPage.empty.securityControls', {
            defaultValue: 'No security control is linked to this task yet.',
          })}
          rows={rows}
          rowKey={(r) => (r._id || r.id) as string}
          columns={[
            {
              key: 'labelScf',
              header: t('tables.SubDomainsList.columns.LabelSCF'),
              render: (r) => (
                <ListPageDescription value={r.LabelSCF} tone="primary" />
              ),
            },
            {
              key: 'label',
              header: t('tables.SubDomainsList.columns.Label'),
              render: (r) => (
                <ListPageDescription value={r.LabelSD || r.Label} />
              ),
            },
            {
              key: 'desc',
              header: t('tables.SubDomainsList.columns.Description'),
              render: (r) => <ListPageDescription value={r.Description} />,
            },
            {
              key: 'targetScore',
              header: t('tables.SubDomainsList.columns.TargetScore'),
              render: (r) => (
                <ListPageDescription
                  value={
                    r.TargetScore != null ? String(r.TargetScore) : undefined
                  }
                />
              ),
            },
            {
              key: 'weight',
              header: t('tables.SubDomainsList.columns.Weight'),
              render: (r) => (
                <ListPageDescription
                  value={r.Weight != null ? String(r.Weight) : undefined}
                />
              ),
            },
          ]}
          actionsHeader={t('tables.SubDomainsList.columns.Action')}
          renderActions={(r) => (
            <DetailRowActions
              roles={roles}
              onEdit={() =>
                navigate(`/subDomain-Maturityscoring/${r._id || r.id}`)
              }
              onDelete={() => handleDeleteClick((r._id || r.id) as string)}
              deleteItem={{
                entityType: t('securityControls', {
                  defaultValue: 'Security control',
                }),
                name: r.LabelSD || r.Label || r.LabelSCF || undefined,
                details: [
                ],
              }}
            />
          )}
        />
      </DetailEntityPanel>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <h4 style={{ margin: '0 0 16px' }}>
            {t('buttons.addSecurityControl')}
          </h4>
          <Stack spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <SelectSecurity
                  value={SecurityIds}
                  onChange={handleSecurityChange1}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                {SecurityIds && (
                  <SecurityControlsSelect
                    value={SecurityControls}
                    onChange={handleSecurityCheckFamilyChange}
                    securityId={SecurityIds}
                  />
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              {SecurityControls && (
                <SelectSubDomain
                  securityControlIds={SecurityControls}
                  selectedSubDomainIds={SubDomainIds}
                  onChange={handleSubDomainChange}
                />
              )}
            </Grid>

            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
            >
              <DetailButton onClick={handleAddSecurityCheckFamilysToAction}>
                {t('buttons.add')}
              </DetailButton>
              <DetailButton variant="ghost" onClick={() => setOpen(false)}>
                {t('buttons.cancel')}
              </DetailButton>
            </div>
          </Stack>
        </Box>
      </Modal>
    </>
  )
}

export default SecurityCheckFamilyListByAction
